/**
 * fetchWrapper
 * 
 * Description:
 * Use to call the API
 * 
 */

import { store, authActions } from "./../store/index";
import { activityLogActions } from "./../store/activityLog.slice";
import HttpStatus from "./../helpers/status.enum";
import { logError, stripOrigin } from "./error-logger";

// Not imported from activity-logger.js on purpose: that module also reads/writes
// this same activityLog.sessionId state, and activityLog.slice.js's thunk already
// imports fetchWrapper — importing activity-logger.js here too would add a second
// module into that require cycle. Reading/writing the redux state directly keeps
// the existing cycle shape unchanged while still sharing the same session id.
function getOrCreateSessionId() {
  const currentSessionId = store.getState().activityLog?.sessionId;
  if (currentSessionId) return currentSessionId;
  const sessionId = crypto.randomUUID();
  store.dispatch(activityLogActions.setSessionId(sessionId));
  return sessionId;
}

export const fetchWrapper = {
  get: request("GET"),
  post: request("POST"),
  put: request("PUT"),
  delete: request("DELETE"),
};

function request(method) {
  return (url, body) => {
    const requestOptions = {
      method,
      headers: authHeader(url),
    };
    requestOptions.headers["X-Frame-Options"] = "SAMEORIGIN"; // prevent clickjacking
    requestOptions.headers["Api-key"] = process.env.REACT_APP_API_SECURITY_TOKEN;
    requestOptions.headers["X-Session-Id"] = getOrCreateSessionId();
    if (body) {
      requestOptions.headers["Content-Type"] = "application/json";      
      requestOptions.body = JSON.stringify(body);
    }
    return fetch(url, requestOptions)
      .then(handleResponse)
      .catch((error) => {
        // Network level failure: offline, dns, cors, request aborted. Redux
        // Toolkit catches this throw inside the thunk, so it never reaches the
        // global unhandledrejection handler. Log it here instead, then rethrow
        // so callers keep behaving exactly as before.
        // The log endpoint is skipped: a failing log call must not log its own
        // failure and loop.
        if (!url.includes("/log-client-error")) {
          logError(error, "ApiRequest", null, "API_ERROR", {
            api_url: stripOrigin(url),
            api_method: method,
          });
        }
        throw error;
      });
  };
}

// Helper functions
function authHeader(url) {
  // Return auth header with jwt if user is logged in and request is to the api url
  const token = authToken();
  const isLoggedIn = !!token;
  const isApiUrl = url.startsWith(process.env.REACT_APP_API_URL);
  if (isLoggedIn && isApiUrl) {
    return { Authorization: `Bearer ${token}` };
  } else {
    return {};
  }
}

function authToken() {
  return store.getState().auth.value?.token;
}

async function handleResponse(response) {
  const isJson = response.headers
    ?.get("content-type")
    ?.includes("application/json");
  const data = isJson ? await response.json() : null;

  // Check for error response
  if (!response.ok) {
    if ([HttpStatus.HTTP_UNAUTHORIZED, HttpStatus.HTTP_FORBIDDEN].includes(response.status) && authToken()) {
      // Auto logout if 401 Unauthorized or 403 Forbidden response returned from api
      const logout = () => store.dispatch(authActions.logout());
      logout();
    }

    /* 
    Get error message from body or default to response status
    const error = (data && data.message) || response.status;
    return Promise.reject(error);
    */

    // Return all error with status code
    const error = data || response.status;
    return error;
  }

  return data;
}
